import React, { useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, ArrowRight } from 'lucide-react';
import Logo, { LogoMark } from '@/components/Logo';
import { PUB } from '@/constants/testIds';

import LanguageSwitcher from '@/components/LanguageSwitcher';
import RuyaWidget from '@/components/RuyaWidget';
import ThemeToggle from '@/components/ThemeToggle';

const PILLARS = [
  { id: 'governance', label: 'Modern Governance' },
  { id: 'risk', label: 'Modern Risk' },
  { id: 'esg', label: 'Modern ESG' },
  { id: 'compliance', label: 'Modern Compliance' },
  { id: 'audit', label: 'Modern Audit' },
  { id: 'leadership', label: 'Modern Leadership' },
];

const INDUSTRIES = [
  'financial-services', 'corporate-service-providers', 'manufacturing', 'energy', 'healthcare', 'technology', 'government',
];

export default function PublicHeader() {
  const [open, setOpen] = useState(false);
  const [drop, setDrop] = useState(null); // 'products' | 'industries' | null
  const loc = useLocation();

  return (
    <header className="sticky top-0 z-50 ve-glass border-b border-slate-200/60">
      <div className="ve-container flex h-16 items-center justify-between">
        <Link to="/" data-testid={PUB.headerLogo} className="shrink-0">
          <Logo noLink />
        </Link>

        <nav className="hidden xl:flex items-center gap-1">
          <div
            className="relative"
            onMouseEnter={() => setDrop('products')}
            onMouseLeave={() => setDrop(null)}
          >
            <button data-testid="nav-products" className="flex items-center gap-1 px-3 py-2 text-sm text-slate-700 hover:text-[#8B3FC5] transition-colors">
              Products <ChevronDown size={14} />
            </button>
            {drop === 'products' && (
              <div className="absolute left-0 top-full mt-1 w-[640px] grid grid-cols-2 gap-1 p-4 bg-white border border-slate-200 rounded-lg shadow-2xl">
                {PILLARS.map((p) => (
                  <Link
                    key={p.id}
                    to={`/modules/${p.id}`}
                    className="group flex items-start justify-between gap-3 p-3 rounded-md hover:bg-slate-50 transition-colors"
                    data-testid={`drop-${p.id}`}
                  >
                    <div>
                      <div className="text-sm font-medium text-slate-900 group-hover:text-[#8B3FC5]">{p.label}</div>
                      <div className="text-xs text-slate-500 mt-0.5">Explore products</div>
                    </div>
                    <ArrowRight size={14} className="mt-1 text-slate-400 group-hover:text-[#8B3FC5] group-hover:translate-x-0.5 transition-transform"/>
                  </Link>
                ))}
              </div>
            )}
          </div>

          <div
            className="relative"
            onMouseEnter={() => setDrop('industries')}
            onMouseLeave={() => setDrop(null)}
          >
            <button data-testid="nav-industries" className="flex items-center gap-1 px-3 py-2 text-sm text-slate-700 hover:text-[#8B3FC5] transition-colors">
              Industries <ChevronDown size={14} />
            </button>
            {drop === 'industries' && (
              <div className="absolute left-0 top-full mt-1 w-[420px] grid grid-cols-2 gap-1 p-4 bg-white border border-slate-200 rounded-lg shadow-2xl">
                {INDUSTRIES.map((id) => (
                  <Link key={id} to={`/industries#${id}`} className="px-3 py-2 text-sm text-slate-700 hover:text-[#8B3FC5] hover:bg-slate-50 rounded-md capitalize">
                    {id.replace(/-/g, ' ')}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <NavLink to="/platform" data-testid="nav-platform" className={({isActive}) => `px-3 py-2 text-sm transition-colors ${isActive ? 'text-[#8B3FC5]' : 'text-slate-700 hover:text-[#8B3FC5]'}`}>Platform</NavLink>
          <NavLink to="/solutions" data-testid="nav-solutions" className={({isActive}) => `px-3 py-2 text-sm transition-colors ${isActive ? 'text-[#8B3FC5]' : 'text-slate-700 hover:text-[#8B3FC5]'}`}>Solutions</NavLink>
          <NavLink to="/pricing" data-testid="nav-pricing" className={({isActive}) => `px-3 py-2 text-sm transition-colors ${isActive ? 'text-[#8B3FC5]' : 'text-slate-700 hover:text-[#8B3FC5]'}`}>Pricing</NavLink>
        </nav>

        <div className="hidden xl:flex items-center gap-3 shrink-0">
          {/* Ru'ya clock is decorative — hide it below 2xl so the login +
              CTA never overflow past the viewport edge on smaller
              desktops / zoomed-in browsers. */}
          <div className="hidden 2xl:block"><RuyaWidget /></div>
          <ThemeToggle compact />
          <LanguageSwitcher />
          <Link to="/login" data-testid="header-login" className="text-sm text-slate-700 hover:text-[#8B3FC5] transition-colors">Login</Link>
          <Link to="/pricing" data-testid={PUB.ctaPricing} className="ve-btn-primary">
            Request Pricing <ArrowRight size={16} />
          </Link>
        </div>

        <button
          className="xl:hidden p-2 text-slate-700 dark:text-white"
          aria-label="Menu"
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="xl:hidden border-t border-slate-200 dark:border-white/10 bg-white dark:bg-[#0A0A0A]">
          <nav className="ve-container flex flex-col py-4 gap-1">
            {/* Theme + language always reachable on mobile */}
            <div className="flex items-center gap-3 px-3 pb-3 mb-2 border-b border-slate-100 dark:border-white/10">
              <ThemeToggle compact />
              <LanguageSwitcher />
            </div>
            <Link to="/platform" className="px-3 py-2 text-sm text-slate-700 dark:text-white/80">Platform</Link>
            <Link to="/solutions" className="px-3 py-2 text-sm text-slate-700 dark:text-white/80">Solutions</Link>
            <Link to="/industries" className="px-3 py-2 text-sm text-slate-700 dark:text-white/80">Industries</Link>
            <Link to="/pricing" className="px-3 py-2 text-sm text-slate-700 dark:text-white/80">Pricing</Link>
            <Link to="/login" className="px-3 py-2 text-sm text-slate-700 dark:text-white/80">Login</Link>
            <Link to="/pricing" className="mt-2 ve-btn-primary justify-center">Request Pricing</Link>
          </nav>
        </div>
      )}
    </header>
  );
}
